import { useEffect, useRef } from 'react'

/**
 * useEdgeTriggeredSubmit — standardised "submit on rising edge of shouldValidate"
 * pattern shared by all interactive renderers.
 *
 * Replaces the 8-line ritual that was repeated in ~28 renderers
 * (RENDERERS_DOMAIN_RC_AUDIT_2026.md §R-03).
 *
 * Contract:
 * - When `shouldValidate` goes from false → true, calls `onAnswer(value)` exactly once.
 * - When `shouldValidate` returns to false (or `resetKey` changes), arms the edge
 *   detector again so the next rising edge re-fires.
 * - `onAnswer` is intentionally read fresh on each rising edge — pass a stable
 *   callback (useCallback) from the parent for best results, but a fresh
 *   reference per render does not cause double-submits because the ref-guard
 *   gates inside the effect.
 *
 * Usage:
 * ```ts
 * useEdgeTriggeredSubmit(value, shouldValidate, onAnswer, question.id)
 * ```
 *
 * For renderers that need to transform `value` before submitting (e.g. parse
 * a comma-decimal, coerce to number), wrap with `useMemo` or compute inside
 * the parent and pass the final payload as `value`.
 */
export function useEdgeTriggeredSubmit<T>(
  value: T,
  shouldValidate: boolean | undefined,
  onAnswer: (v: T) => void,
  resetKey: string | number,
): void {
  const prevValidateRef = useRef(false)

  // Reset edge tracking when the question changes.
  useEffect(() => {
    prevValidateRef.current = false
  }, [resetKey])

  // Reset edge tracking when validation is cleared.
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false
  }, [shouldValidate])

  // Fire onAnswer on the rising edge of shouldValidate.
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true
      onAnswer(value)
    }
  }, [shouldValidate, value, onAnswer])
}
